import { useEffect } from "react";
import { Link, useRouteError } from "react-router-dom";

import { PageHead, Shell } from "@ui/react";
import { useAuth } from "../lib/auth";
import { parseOperatingMode } from "../lib/types/enums";
import { captureError } from "../lib/errors";
import { errorMessage } from "../lib/errorMessage";

/**
 * The router's error boundary, and the 404 route.
 *
 * A thrown render error is captured before display: on a client-routed app the
 * user's instinct is to reload, which wipes the console — so if it is not
 * recorded here, it is gone.
 *
 * It renders the SAME shell as everything else, in its `auth` variant. This
 * page used to be a bare `<main>` with an icon, a 20px title and one orange
 * button in the top 280px of an otherwise empty screen: no Shoku mark, no
 * footer, and a "Go to jobs" button that is the wrong destination for a
 * bookable contractor, who has no job feed.
 */
export function RouteError({ notFound = false }: { notFound?: boolean }) {
  const error = useRouteError();
  const { gate, contractor } = useAuth();

  // In an effect keyed on the error, not in render. useAuth() below re-renders
  // this component every time the auth gate moves — loading to ready on the
  // first load alone — and reporting from the render body filed the same render
  // failure again on each pass.
  useEffect(() => {
    if (!notFound && error) captureError("Route render failed", error);
  }, [notFound, error]);

  const signedIn = gate === "ready";
  const home = signedIn
    ? parseOperatingMode(contractor?.operatingMode) === "bookable"
      ? "/bookings"
      : "/jobs"
    : "/login";
  const homeLabel = signedIn ? "Back to your workspace" : "Go to sign in";

  return (
    <Shell variant="auth" brandHref={home}>
      <PageHead
        eyebrow={notFound ? "404" : "Error"}
        title={notFound ? "Page not found" : "Something went wrong"}
        lead={
          notFound
            ? "That page doesn't exist. It may have moved, or the link may be out of date."
            : errorMessage(error)
        }
        actions={
          <Link className="btn btn-primary" to={home}>
            {homeLabel}
          </Link>
        }
      />
    </Shell>
  );
}
